import React from 'react';
import { ClipboardCheck, Users, MapPin, Phone, ArrowRight, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navigation from '../../components/Navigation';
import Footer from '../../components/Footer';
import SEO from '../../components/SEO';
import FAQRichSnippet from '../../components/FAQRichSnippet';

const CASE_MGMT_FAQS = [
  { q: "What does a case manager do?", a: "A case manager coordinates your care across providers and agencies, helps you access housing, benefits, transportation, and community resources, and ensures your treatment plan is being followed effectively." },
  { q: "Is case management covered by insurance?", a: "Yes. Case management is covered by Medicaid and most Maryland HealthChoice managed care organizations. Contact us to verify your specific coverage." },
  { q: "How is case management different from therapy?", a: "Therapy focuses on emotional processing and coping strategies. Case management focuses on practical coordination — connecting you with housing, benefits, medical care, legal resources, and other community supports." },
];

const CaseManagementPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Case Management Services"
        description="Mental health case management in Maryland. SDORA Wellbeing provides care coordination, resource navigation, benefits assistance, and community support in Pikesville and Baltimore County. Accepting Medicaid."
        path="/services/case-management"
      />
      <Navigation />

      {/* Hero */}
      <div className="bg-gradient-to-br from-teal-600 to-blue-700 text-white py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <ClipboardCheck className="w-12 h-12 mx-auto mb-4 opacity-80" />
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">Case Management</h1>
          <p className="text-lg text-teal-100 max-w-2xl mx-auto">
            Coordinated support to help you navigate services, access resources, and build a stable path toward recovery and independence.
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* What is Case Management */}
        <section className="mb-12">
          <h2 className="text-lg font-bold text-gray-900 mb-4">What Is Mental Health Case Management?</h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            Case management connects individuals living with mental health conditions to the services, resources, and supports they need. 
            At SDORA Wellbeing, our case managers work alongside you and your treatment team to coordinate care, reduce barriers, 
            and help you reach your personal goals.
          </p>
          <p className="text-gray-700 leading-relaxed">
            Whether you need help accessing housing, benefits, transportation, medical care, or community programs, 
            your case manager serves as a consistent point of contact who understands your situation and advocates on your behalf.
          </p>
        </section>

        {/* Services Provided */}
        <section className="mb-12">
          <h2 className="text-lg font-bold text-gray-900 mb-4">How Case Management Can Help</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              "Care coordination across providers and agencies",
              "Resource identification and referral",
              "Benefits and insurance navigation assistance",
              "Housing and transportation support",
              "Crisis planning and prevention support",
              "Appointment scheduling and follow-up",
              "Connection to community programs and peer support",
              "Advocacy with service providers and systems",
            ].map((item) => (
              <div key={item} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                <CheckCircle className="w-4 h-4 text-teal-600 mt-0.5 flex-shrink-0" />
                <span className="text-sm text-gray-700">{item}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Who Benefits */}
        <section className="mb-12">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Who Can Benefit?</h2>
          <p className="text-gray-700 leading-relaxed mb-3">
            Case management services are available to individuals who need additional support navigating complex systems 
            or managing multiple service needs. This often includes individuals receiving OMHC therapy or participating 
            in a Psychiatric Rehabilitation Program (PRP) who may benefit from coordinated support.
          </p>
          <p className="text-gray-700 leading-relaxed">
            Services may be provided in-person, by phone, or through telehealth depending on your needs and circumstances.
          </p>
        </section>

        {/* Insurance */}
        <section className="mb-12 bg-teal-50 rounded-xl p-6 border border-teal-100">
          <h2 className="text-lg font-bold text-gray-900 mb-2">Insurance & Coverage</h2>
          <p className="text-sm text-gray-700 mb-3">
            SDORA Wellbeing accepts Medicaid, Maryland HealthChoice managed care organizations, and most major insurance plans 
            for case management services. Contact us to verify your specific coverage.
          </p>
          <Link to="/insurance" className="text-sm text-teal-700 font-semibold hover:underline inline-flex items-center gap-1">
            View accepted insurance plans <ArrowRight className="w-3 h-3" />
          </Link>
        </section>

        {/* CTA */}
        <section className="text-center bg-gray-50 rounded-xl p-8 border border-gray-200">
          <h2 className="text-lg font-bold text-gray-900 mb-2">Get Connected to Case Management</h2>
          <p className="text-sm text-gray-600 mb-4">Call us or submit a referral to learn how case management can support your treatment and recovery.</p>
          <div className="flex flex-wrap gap-3 justify-center">
            <a href="tel:+18777725150" className="inline-flex items-center gap-2 bg-teal-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-teal-700 transition-colors">
              <Phone className="w-4 h-4" /> (877) 772-5150
            </a>
            <Link to="/services/referrals" className="inline-flex items-center gap-2 bg-white text-teal-600 border border-teal-200 px-6 py-3 rounded-lg font-semibold hover:bg-teal-50 transition-colors">
              Submit Referral <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>
        <FAQRichSnippet faqs={CASE_MGMT_FAQS} />
      </div>

      <Footer />
    </div>
  );
};

export default CaseManagementPage;
